import { _ as __rest } from '../tslib.es6-C8rd5pYo.mjs';
import { keyframes, css, jsx } from '@emotion/react';
import { Colors, Zindexes, Animation } from '@zapier/design-system';
import React, { useContext } from 'react';
import { ModalContext, ModalOverlay, Modal, OverlayTriggerStateContext, Dialog } from 'react-aria-components';
import { IconButton } from '../IconButton/IconButton.es.js';
import { BREAKPOINTS } from '../constants.es.js';
import { ModalDialogBody } from './Body.es.js';
import { ModalDialogFooter } from './Footer.es.js';
import { ModalDialogHeader } from './Header.es.js';
import { ModalDialogTrigger } from './Trigger.es.js';

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;
const fadeOut = keyframes`
  from { opacity: 1; }
  to { opacity: 0; }
`;
const OPACITY_80_PERCENT = "CC";
const ModalOverlayStyles = css({
  position: "fixed",
  top: 0,
  left: 0,
  width: "100vw",
  height: "100vh",
  backgroundColor: `var(--zds-background-overlay, ${Colors.GrayWarm10}${OPACITY_80_PERCENT})`,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: Zindexes.modalOverlay,
  "&[data-entering]": {
    animation: `${fadeIn} ${Animation.transitionDuration}`
  },
  "&[data-exiting]": {
    animation: `${fadeOut} ${Animation.transitionDuration} ${Animation.transitionTimingFunction}`
  }
});
const ModalStyles = css({
  position: "relative",
  borderRadius: "5px",
  maxWidth: "calc(100vw - var(--zds-space-20, 20px))",
  maxHeight: "min(650px, calc(100vh - 70px))",
  backgroundColor: `var(--zds-background-weaker, ${Colors.GrayWarm1})`,
  outline: "none",
  boxShadow: "-8px 0 20px rgba(0 0 0 / 0.1)",
  [`@media screen and (min-width: ${BREAKPOINTS.tablet})`]: {
    minWidth: "550px",
    maxWidth: "650px",
    margin: "0px var(--zds-space-20, 20px)"
  }
});
const DialogStyles = css({
  display: "flex",
  flexDirection: "column",
  maxHeight: "inherit",
  padding: "var(--zds-space-20, 20px)",
  [`@media screen and (min-width: ${BREAKPOINTS.tablet})`]: {
    padding: "var(--zds-space-36, 40px)"
  }
});
const CloseButtonStyles = css({
  position: "absolute",
  top: "var(--zds-space-8, 8px)",
  right: "var(--zds-space-8, 8px)",
  zIndex: Zindexes.closeButton
});
const ModalDialogRoot = (_a) => {
  var { children, className, isDismissable = true, isOpen, onOpenChange } = _a, props = __rest(_a, ["children", "className", "isDismissable", "isOpen", "onOpenChange"]);
  const contextValue = React.useMemo(() => ({ isDismissable }), [isDismissable]);
  return jsx(
    ModalContext.Provider,
    { value: contextValue },
    jsx(
      ModalOverlay,
      { css: ModalOverlayStyles, "data-zds": true, isOpen, isKeyboardDismissDisabled: !isDismissable, onOpenChange },
      jsx(
        Modal,
        { css: ModalStyles, "data-zds": true, className },
        jsx(ModalDialogInner, { "aria-label": props["aria-label"], "aria-labelledby": props["aria-labelledby"] }, children)
      )
    )
  );
};
const ModalDialogInner = (_a) => {
  var { children } = _a, props = __rest(_a, ["children"]);
  const { close } = useContext(OverlayTriggerStateContext);
  const modalContext = useContext(ModalContext);
  return jsx(
    Dialog,
    { "aria-label": props["aria-label"], "aria-labelledby": props["aria-labelledby"], css: DialogStyles, "data-zds": true },
    (modalContext === null || modalContext === void 0 ? void 0 : modalContext.isDismissable) && jsx(IconButton, { css: CloseButtonStyles, name: "formX", onPress: close, variant: "ghost" }),
    typeof children === "function" ? children({ close }) : children
  );
};
const ModalDialog = {
  /**
   * The dialog's main content.
   */
  Body: ModalDialogBody,
  /**
   * The dialog footer, which should contain up to two buttons:
   * - an optional secondary button
   * - an required primary button
   */
  Footer: ModalDialogFooter,
  /**
   * The dialog header containing a descriptive title of the dialog content.
   */
  Header: ModalDialogHeader,
  /**
   * A modal dialog shown above other content in an application.
   */
  Root: ModalDialogRoot,
  /**
   * A trigger container that listens for a button press to open the modal dialog. It should wrap around a `design-system-beta` `Button` and `Modal.Root`.
   *
   * **Should only be used when the `ModalDialog`'s open state is uncontrolled**
   */
  Trigger: ModalDialogTrigger
};

export { ModalDialog, ModalDialogRoot, ModalDialog as default };
//# sourceMappingURL=ModalDialog.es.js.map
